import { useState, useEffect } from 'react'
import { BidAPI } from '@certi/shared/api/bid/BidAPI'
import { OrderAPI } from '@certi/shared/api/order/OrderAPI'
import type { UserBidItemResponse, UserOrderItemResponse } from '@certi/shared/types'
import { useAuth } from '@/hooks/useAuth'
import { Link, useNavigate } from 'react-router-dom'

export default function BidsSection() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'live' | 'won' | 'closed'>('live')
  const [liveBids, setLiveBids] = useState<UserBidItemResponse[]>([])
  const [wonBids, setWonBids] = useState<UserBidItemResponse[]>([])
  const [closedBids, setClosedBids] = useState<UserBidItemResponse[]>([])
  const [unpaidOrdersByAuctionId, setUnpaidOrdersByAuctionId] = useState<Record<string, UserOrderItemResponse>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!session?.accessToken) return

    const fetchBids = async () => {
      try {
        setLoading(true)
        setError(null)

        if (session.accessToken === 'fake-access-token') {
          // Mock data for dev login
          setLiveBids([])
          setWonBids([])
          setClosedBids([])
          setUnpaidOrdersByAuctionId({})
        } else {
          const [live, won, closed, unpaidOrders] = await Promise.all([
            BidAPI.getUserLiveBids(session.accessToken),
            BidAPI.getUserWonBids(session.accessToken),
            BidAPI.getUserClosedBids(session.accessToken),
            OrderAPI.getUserUnpaidOrders(session.accessToken),
          ])
          setLiveBids(live)
          setWonBids(won)
          setClosedBids(closed)
          setUnpaidOrdersByAuctionId(
            unpaidOrders.reduce<Record<string, UserOrderItemResponse>>((acc, order) => {
              acc[order.auctionId] = order
              return acc
            }, {})
          )
        }
      } catch (err) {
        console.error('Failed to fetch bids:', err)
        setError(err instanceof Error ? err.message : 'Failed to load bids')
      } finally {
        setLoading(false)
      }
    }

    fetchBids()
  }, [session?.accessToken])

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date)
  }

  const getBidDisplayStatus = (bid: UserBidItemResponse) => {
    if (bid.isWinner || bid.bidStatus === 'WON') {
      return { label: 'Won', className: 'text-green-600' }
    }

    if (bid.isLeading) {
      return { label: 'Leading', className: 'text-green-600' }
    }

    if (bid.bidStatus === 'OUTBID') {
      return { label: 'OUTBID', className: 'text-red-600' }
    }

    return { label: bid.bidStatus, className: 'text-gray-600' }
  }

  const canPayForOrder = (order?: UserOrderItemResponse) =>
    !!order && !order.isPaid && (order.status === 'PENDING' || order.status === 'PROCESSING')

  const handlePayNow = (paymentId: string) => {
    if (!session?.accessToken) {
      navigate('/login')
      return
    }

    navigate(`/checkout/${paymentId}`)
  }

  const renderBidItem = (bid: UserBidItemResponse) => (
    <div
      key={bid.bidId}
      className="bg-white border border-gray-200 rounded-lg p-4"
    >
      {(() => {
        const statusDisplay = getBidDisplayStatus(bid)
        const unpaidOrder = unpaidOrdersByAuctionId[bid.auctionId]
        const showPayNow = canPayForOrder(unpaidOrder)
        const showPreparingCheckout = bid.isWinner && bid.auctionStatus === 'CLOSED' && !showPayNow

        return (
          <>
      <div className="flex gap-4">
        {bid.listingImageUrl ? (
          <Link to={`/marketplace/${bid.listingSlug}`} className="shrink-0">
            <img
              src={bid.listingImageUrl}
              alt={bid.listingTitle}
              className="w-24 h-24 object-cover rounded"
            />
          </Link>
        ) : null}
        <div className="flex-1">
          <Link
            to={`/marketplace/${bid.listingSlug}`}
            className="font-semibold text-gray-900 mb-1 inline-block hover:text-brand-primary transition-colors"
          >
            {bid.listingTitle}
          </Link>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Your bid:</span>
              <span className="font-medium">{formatPrice(bid.myBidAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Current price:</span>
              <span className="font-medium">{formatPrice(bid.currentPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`font-medium ${statusDisplay.className}`}>
                {statusDisplay.label}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Ends:</span>
              <span className="text-gray-900">{formatDate(bid.auctionEndsAt)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between gap-3">
        <Link
          to={`/marketplace/${bid.listingSlug}`}
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          View item
        </Link>
        {showPayNow ? (
          <button
            type="button"
            onClick={() => handlePayNow(unpaidOrder.paymentId)}
            className="btn btn-primary"
          >
            Pay now
          </button>
        ) : showPreparingCheckout ? (
          <span className="text-sm text-gray-500">Preparing checkout...</span>
        ) : null}
      </div>
          </>
        )
      })()}
    </div>
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">My Bids</h2>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('live')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'live'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Live
          </button>
          <button
            onClick={() => setActiveTab('won')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'won'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Won
          </button>
          <button
            onClick={() => setActiveTab('closed')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'closed'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Closed
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'live' && (
          <div>
            {liveBids.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No live bids</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  You don't have any live bids at the moment. When you place a bid on a live product, it will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {liveBids.map(renderBidItem)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'won' && (
          <div>
            {wonBids.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No won bids</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  You don't have any won bids yet. When you win an auction, the product will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {wonBids.map(renderBidItem)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'closed' && (
          <div>
            {closedBids.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No closed bids</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  You don't have any closed bids yet. All the closed products you've bid on will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {closedBids.map(renderBidItem)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
